/* Copyright (c) 2026 Xperi. All Rights Reserved. */

import type { FunctionComponent, JSX } from "react";

import { useUnitySelector } from "../../../viewModel";
import {
  selectIsOsdVisible,
  selectOsdCode,
  selectOsdEasCountry,
  selectIsEasVideoVisible,
  selectOsdEasIsDismissible,
  selectOsdMessage,
  selectOsdRepositionRequired
} from "../../../viewModel/osd";
import { EasOsd, EasOsdTemplateSelector } from "./eas";
import type { EasOsdTemplateConfiguration } from "./eas";
import Osd from "./Osd";

// eslint-disable-next-line @typescript-eslint/no-unused-vars -- Required by CodingGuidelines §7 (D-09)
const TAG: string = "OsdContainer";

/**
 * OsdContainer — connected wrapper around the presentational `Osd`
 * component.
 *
 * Mounted ONCE at the app shell (`src/views/App.tsx`), like
 * `ToastMessageContainer` — takes no props. Any screen shows the OSD by
 * dispatching `showOsd`/`hideOsd` into the shared `osd` Redux slice; this
 * container is a pure reflection of that state, mirroring kaveri's
 * `{isErrorOSDActive && <Osd .../>}` pattern but without a per-screen mount
 * point or props channel.
 *
 * No branding logo is passed today (no unity-web requirement for it) — see
 * `OsdProps.brandingLogoUrl` for the dormant-but-wired re-enable path.
 *
 * Renders the country-specific `EasOsd` template instead of the generic
 * `Osd` whenever the slice's `easCountry` is set (via `showEasOsd`) — an EAS
 * alert reuses the same slice, mount point and priority arbitration rather
 * than a parallel one, and `EAS_OSD_PRIORITY` guarantees it is never
 * outranked by anything else that could be showing.
 */
const OsdContainer: FunctionComponent = (): JSX.Element | null => {
  const isVisible: boolean = useUnitySelector(selectIsOsdVisible);
  const message: string = useUnitySelector(selectOsdMessage);
  const code: string = useUnitySelector(selectOsdCode);
  const easCountry: string = useUnitySelector(selectOsdEasCountry);
  const hasEasVideo: boolean = useUnitySelector(selectIsEasVideoVisible);
  const easIsDismissible: boolean = useUnitySelector(selectOsdEasIsDismissible);
  const isRepositionRequired: boolean = useUnitySelector(
    selectOsdRepositionRequired
  );

  if (isVisible === false) {
    return null;
  }

  const isEasAlert: boolean = easCountry !== "";
  if (isEasAlert) {
    const configuration: EasOsdTemplateConfiguration =
      EasOsdTemplateSelector.getInstance().selectByCountry(easCountry);

    return (
      <EasOsd
        key={easCountry}
        message={message}
        configuration={configuration}
        id={code}
        hasVideo={hasEasVideo}
        isDismissible={easIsDismissible}
      />
    );
  }

  return (
    <Osd
      key={isRepositionRequired ? "floating" : "anchored"}
      message={message}
      code={code}
      enableAntiBurnReposition={isRepositionRequired}
    />
  );
};

export default OsdContainer;
